import { useAuth } from "@/providers/auth-provider";
import { Link, useLocation } from "wouter";
import {
  LayoutDashboard,
  Users,
  Building2,
  CreditCard,
  Shield,
  Megaphone,
  BookOpen,
  LogOut,
  ChevronRight,
} from "lucide-react";

interface AdminLayoutProps {
  children: React.ReactNode;
}

const adminNavItems = [
  { href: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { href: "/admin/client-management", label: "Clients", icon: Users },
  { href: "/dashboard/subscription-plans", label: "Subscription Plans", icon: CreditCard },
  { href: "/dashboard/customer-subscriptions", label: "Customer Subscriptions", icon: Building2 },
  { href: "/admin/compliance", label: "Compliance", icon: Shield },
  { href: "/admin/coa-templates", label: "COA Templates", icon: BookOpen },
  { href: "/zentro-admin/announcements", label: "Announcements", icon: Megaphone },
];

export function AdminLayout({ children }: AdminLayoutProps) {
  const { user, logout } = useAuth();
  const [location] = useLocation();

  return (
    <div className="min-h-screen flex">
      {/* Admin Sidebar */}
      <aside className="hidden md:flex w-64 flex-col bg-slate-900 text-white">
        {/* Logo */}
        <div className="p-4 border-b border-slate-700">
          <h1 className="text-lg font-bold">ZentroBooks</h1>
          <p className="text-xs text-slate-400 mt-1">Platform Admin</p>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {adminNavItems.map((item) => {
            const isActive = location === item.href;
            const Icon = item.icon;
            return (
              <Link key={item.href} href={item.href}>
                <div
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm cursor-pointer transition-colors ${
                    isActive
                      ? "bg-blue-600 text-white"
                      : "text-slate-300 hover:bg-slate-800 hover:text-white"
                  }`}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span>{item.label}</span>
                  {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                </div>
              </Link>
            );
          })}
        </nav>

        {/* User footer */}
        <div className="p-4 border-t border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-sm font-medium">
              {user?.fullName?.[0] || user?.email?.[0] || "A"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.fullName || "Admin"}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
            <button
              onClick={() => logout()}
              className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto bg-gray-50">
        {/* Top bar (mobile) */}
        <div className="md:hidden flex items-center justify-between p-4 bg-slate-900 text-white">
          <h1 className="text-lg font-bold">ZentroBooks Admin</h1>
        </div>
        <div className="p-4 md:p-6">
          {children}
        </div>
      </main>
    </div>
  );
}
